import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { Send } from 'lucide-react-native';
import { useEffect, useRef, useState, type ReactElement, type ReactNode, type Ref } from 'react';
import {
  Platform,
  type NativeSyntheticEvent,
  type TextInput,
  type TextInputProps,
  type TextInputEndEditingEventData,
  type TextInputKeyPressEventData,
} from 'react-native';
import { twJoin } from 'tailwind-merge';

import { GlassIconButton, useLiquidGlass } from '@/components/ui/glass';
import { HStack } from '@/components/ui/hstack';
import { Textarea, TextareaInput } from '@/components/ui/textarea';
import { View } from '@/components/ui/view';

/**
 * Past this input height the text has wrapped to a second line, so Send drops
 * below it. One line of `text-base` (24px) plus the input's 24px of vertical
 * padding is 48; two is 72, so the cut sits between them.
 */
export const COMPOSER_EXPAND_HEIGHT = 60;

/** One line of `text-base` plus the input's vertical padding: the resting height. */
const COMPOSER_LINE_HEIGHT = 48;

/**
 * Pins an empty input to one line. On iOS a multiline input that is cleared
 * in code, as every send clears it, keeps the height its old text gave it, so
 * without this the box stays several lines tall after a long message is sent.
 */
const EMPTY_INPUT_STYLE = { height: COMPOSER_LINE_HEIGHT };

/**
 * On web the collapsed button row is inert so clicks reach the input beneath
 * it, and each button turns events back on. A prop or style for this never
 * reached the DOM through the stack primitive, and a `box-none` class compiles
 * to nothing, so only plain CSS works. Native keeps its own touch handling.
 */
const WEB_BUTTON_CLASS = 'web:pointer-events-auto';

export type ChatComposerInputProps = {
  value: string;
  onChangeText: (text: string) => void;
  /** Pressing Send, or Enter on web. Not called while `canSubmit` is false. */
  onSubmit: () => void;
  canSubmit: boolean;
  placeholder: string;
  maxLength: number;
  nativeID: string;
  inputLabel: string;
  sendLabel: string;
  /** Why Send is off, for screen readers; nothing under the box says it. */
  sendHint?: string;
  /**
   * Wraps the Send button, for a caller that needs to mark it, as Research's
   * tutorial does to highlight it. The button keeps its own place in the row.
   */
  wrapSendButton?: (sendButton: ReactElement) => ReactElement;
  /** A control at the start of the box, opposite Send, such as an attach button. */
  leadingAction?: ReactNode;
  /**
   * Puts the cursor back in the box after a Send press on iOS, which blurs it
   * to commit autocorrect. Set it where the keyboard stays open after a send.
   */
  refocusAfterSubmit?: boolean;
};

/**
 * The text box and Send button shared by the app's message composers. It owns
 * only typing and submitting; what a send means, its limits and its failures
 * belong to the caller, as does closing the keyboard afterwards.
 *
 * On web Enter sends and Shift+Enter breaks the line. Native keeps the return
 * key as a newline, since messages there are often several paragraphs.
 *
 * Once the text wraps to a second line the actions move below it, and it stays there until the box is
 * emptied: the input widens when Send leaves its row, which can rewrap the
 * text back to one line and would otherwise flip the layout on every
 * keystroke. Emptying it, by sending or by deleting, returns it to one line.
 * Size reports that arrive while it is empty are ignored, because iOS still
 * reports the cleared text's height for a moment and would re-expand it.
 *
 * The box is a pill while it holds one line: a `leadingAction` at the start,
 * the text, then Send, all in one row. Once the text wraps it grows upward into
 * a rounded panel, text above and the action row below, as in Claude's composer.
 * The row stays the same element in both forms, so the input keeps focus as it
 * expands.
 *
 * iOS applies a pending autocorrection only when the box loses focus or a
 * word ends, and tapping Send does neither. Submitting straight away sends the
 * uncorrected word, and the correction then lands in the box just cleared. So
 * a Send press on iOS blurs the box first, takes the text from its end-editing
 * event, and submits on the render that follows, when the caller's handler can
 * see it. Enter on web and other platforms submit at once.
 *
 * The input sizes to its own text, up to its max height. The Textarea
 * primitive makes its input `flex-1`, which on Android pins it at the box's
 * minimum height, so long text sat on one line scrolled to its end instead of
 * growing.
 *
 * Where Liquid Glass is drawn, the caller's glass panel is the box's
 * background. Elsewhere the box is white on the screen's tinted background.
 */
export function ChatComposerInput({
  value,
  onChangeText,
  onSubmit,
  canSubmit,
  placeholder,
  maxLength,
  nativeID,
  inputLabel,
  sendLabel,
  sendHint,
  wrapSendButton,
  leadingAction,
  refocusAfterSubmit = false,
}: ChatComposerInputProps) {
  const hasGlass = useLiquidGlass();

  const [hasWrapped, setHasWrapped] = useState(false);

  const isEmpty = value === '';

  const isExpanded = hasWrapped;

  if (isEmpty && hasWrapped) {
    setHasWrapped(false);
  }

  const inputRef = useRef<TextInput>(null);

  const isAwaitingCommit = useRef(false);

  const [isCommitted, setIsCommitted] = useState(false);

  const submit = () => {
    if (canSubmit) {
      onSubmit();
    }
  };

  useEffect(() => {
    if (!isCommitted) {
      return;
    }

    setIsCommitted(false);

    submit();

    if (refocusAfterSubmit) {
      inputRef.current?.focus();
    }
  });

  const handleEndEditing = (event: NativeSyntheticEvent<TextInputEndEditingEventData>) => {
    if (!isAwaitingCommit.current) {
      return;
    }

    isAwaitingCommit.current = false;

    onChangeText(event.nativeEvent.text);

    setIsCommitted(true);
  };

  const pressSend = () => {
    if (Platform.OS === 'ios' && inputRef.current?.isFocused()) {
      isAwaitingCommit.current = true;

      inputRef.current.blur();

      return;
    }

    submit();
  };

  const handleKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    const nativeEvent = event.nativeEvent as TextInputKeyPressEventData & { shiftKey?: boolean };

    if (isWeb && nativeEvent.key === 'Enter' && !nativeEvent.shiftKey) {
      event.preventDefault();

      submit();
    }
  };

  const sendButton = (
    <GlassIconButton
      icon={Send}
      onPress={pressSend}
      disabled={!canSubmit}
      tone="primary"
      accessibilityLabel={sendLabel}
      accessibilityHint={sendHint}
      fallbackClassName="bg-primary-500"
      iconClassName="h-5 w-5 text-typography-0"
    />
  );

  return (
    <View
      className={twJoin(
        'w-full rounded-[28px]',
        !isExpanded && 'relative',
        !hasGlass && 'bg-background-0 shadow-soft-1'
      )}>
      <Textarea
        className={twJoin(
          'h-auto min-h-[48px] border-0 bg-transparent data-[focus=true]:border-0 data-[focus=true]:web:ring-0'
        )}
        size="md">
        <TextareaInput
          // The primitive types its ref by props, but the instance is the native TextInput.
          ref={inputRef as unknown as Ref<TextInputProps>}
          nativeID={nativeID}
          aria-label={inputLabel}
          placeholder={placeholder}
          value={value}
          onChangeText={onChangeText}
          onKeyPress={handleKeyPress}
          onEndEditing={handleEndEditing}
          onContentSizeChange={(event) => {
            if (isEmpty) {
              return;
            }

            const isTall = event.nativeEvent.contentSize.height > COMPOSER_EXPAND_HEIGHT;

            setHasWrapped((wasWrapped) => isTall || wasWrapped);
          }}
          style={isEmpty ? EMPTY_INPUT_STYLE : undefined}
          maxLength={maxLength}
          className={twJoin(
            'no-scrollbar max-h-64 flex-none py-3 text-base text-typography-900',
            // The collapsed row overlays the input, so its side padding (pl-14, pr-14) must
            // clear the 40px round buttons plus the row's 4px inset; keep them in step.
            isExpanded ? 'px-4' : twJoin('pr-14', leadingAction ? 'pl-14' : 'pl-4')
          )}
          multiline
        />
      </Textarea>

      <HStack
        className={twJoin(
          'items-center',
          leadingAction ? 'justify-between' : 'justify-end',
          isExpanded ? 'px-2 pb-2' : 'absolute bottom-0 left-0 right-0 p-1 web:pointer-events-none'
        )}>
        {leadingAction ? <View className={WEB_BUTTON_CLASS}>{leadingAction}</View> : null}

        <View className={WEB_BUTTON_CLASS}>
          {wrapSendButton ? wrapSendButton(sendButton) : sendButton}
        </View>
      </HStack>
    </View>
  );
}
